import { useEffect, useState } from 'react'
import Image from 'next/image'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { DataPacket_Kind, RemoteParticipant, RoomEvent } from 'livekit-client'
import { useMaybeRoomContext } from '@/src/context'
import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectBranding, selectIsAdmin } from '@/src/store/slices/meetData'
import { selectRoomType, selectV } from '@/src/store/slices/room'
import { setPopupText, selectPopupText } from '@/src/store/slices/popupText'

const Popup = () => {
  const [isOpen, setIsOpen] = useState<boolean>(true)
  const [isHidden, setIsHidden] = useState<boolean>(false)

  const room = useMaybeRoomContext()
  const dispatch = useAppDispatch()

  const { text_primary_color } = useAppSelector(selectBranding)
  const isAdmin = useAppSelector(selectIsAdmin)
  const roomType = useAppSelector(selectRoomType)
  const v = useAppSelector(selectV)
  const popupText = useAppSelector(selectPopupText)
  const isCurator = room?.localParticipant?.attributes?.isCurator === 'true'
  const canSeePopup = isAdmin || (v === 'hr' && isCurator)
  const hideLegacyPopup = isAdmin && roomType === 'textAssistant'

  useEffect(() => {
    if (!room) {
      return
    }

    const handleDataReceived = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      if (topic?.includes('lk-popup-chat-topic')) {
        try {
          const decoder = new TextDecoder('utf-8')
          const text = JSON.parse(decoder.decode(payload))

          dispatch(setPopupText(text.message))
          setIsOpen(true)
        } catch (e) {
          console.error('Error with decoding message: \n', e)
        }
      }
    }

    room?.on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      room?.off(RoomEvent.DataReceived, handleDataReceived)
    }
  }, [room, dispatch])

  return (
    <AnimatePresence>
      {isOpen && popupText?.trim()?.length > 0 && canSeePopup && !hideLegacyPopup && (
        <motion.div
          className={clsx('lk-toast-connection-state lk-toast lk-popup-mvp', {
            'lk-popup-mvp-hidden': isHidden,
          })}
          transition={{ duration: 0.25 }}
        >
          {isHidden ? (
            <motion.button
              className="lk-popup-mvp-hidden-btn"
              onClick={() => setIsHidden(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <Image src="/icons/format-align-left.svg" alt="Show hints" width={16} height={16} />
            </motion.button>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.05 }}
              className="lk-popup-mvp-content"
            >
              <p style={{ color: `${text_primary_color}` }}>Прочитайте вашу речь:</p>
              <div style={{ color: `${text_primary_color}` }} className="lk-popup-mvp-textGpt">
                {popupText}
              </div>

              <div className="lk-popup-buttons lk-popup-mvp-button">
                <button className="lk-button" onClick={() => setIsHidden(true)}>
                  Скрыть
                </button>

                <button className="lk-button" onClick={() => setIsOpen(false)}>
                  Закрыть
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default Popup
